import { useContext, useState, useEffect } from "react";
import UserContext from "../../hooks/UserContext";
import { getZones } from "../../scripts/zone";
import { getUserObjectRestrictionsByCustomer, addUserObjectRestriction, deleteUserObjectRestriction } from "../../scripts/userObjectRestriction";
import { getUsersByCustomerId } from "../../scripts/user";
import { Placeholder, StyledTable, HeaderRow } from "../installation/table/styles/TableStyles";
import { TableCell } from "../installation/fields/Styles";
import Checkbox from "../UiComponents/Checkbox";
import { FaCheck } from 'react-icons/fa';
import { getUserDisplayName } from "../settings/user/userAdministration/userAdministrationUtils";
import theme from "../../UI/theme";
import styled from "styled-components";
import { getMusicPlayers } from "../../scripts/musicPlayer";

const ZoneHeaderCell = styled(TableCell)`
    writing-mode: horizontal-tb;
    text-orientation: mixed;
    white-space: nowrap;
    max-width: 120px;
    min-width: 80px;
    padding: 8px 4px;
    text-align: center;
    word-break: break-word;
    hyphens: auto;
    overflow-wrap: break-word;
`;

const RestrictionCheckbox = styled.div`
    display: inline-block;
    width: 1.5rem;
    height: 1.5rem;
    background: ${theme.colors.darkSpace80};
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid transparent;
    
    &:hover {
        border-color: ${theme.colors.greenEnergy};
    }
`;

const CheckIcon = styled.svg`
    fill: none;
    width: 100%;
    stroke: ${theme.colors.greenEnergy};
    stroke-width: 4px;
    visibility: ${props => props.visible ? 'visible' : 'hidden'};
    opacity: ${props => props.visible ? '100%' : '0%'};
    transition: opacity 0.2s ease;
`;

const UserCell = styled(TableCell)`
    min-width: 150px;
    font-weight: 500;
`;

const CheckboxCell = styled(TableCell)`
    text-align: center;
    padding: 4px 2px;
    width: 60px;
    vertical-align: middle;
    
    > div {
        display: inline-block;
        margin: 0 auto;
    }
`;

const TableContainer = styled.div`
    /* Responsive height - leaves space for other UI elements */
    min-height: 300px;
    overflow: auto;
    border: 1px solid ${theme.colors.darkSpace80};
    border-radius: 4px;
    position: relative;
`;

const StickyTable = styled(StyledTable)`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    
    /* Sticky header row - stays at top when scrolling vertically */
    thead {
        position: sticky;
        top: 0;
        z-index: 10;
    }
    
    thead th {
        position: sticky;
        top: 0;
        background-color: ${theme.colors.darkSpace90} !important;
        z-index: 10;
        border-bottom: 2px solid ${theme.colors.darkSpace80};
    }
    
    /* Sticky first column - stays at left when scrolling horizontally */
    tbody tr td:first-child,
    thead tr th:first-child {
        position: sticky;
        left: 0;
        background-color: ${theme.colors.darkSpace90} !important;
        z-index: 5;
        border-right: 2px solid ${theme.colors.darkSpace80};
    }
    
    /* Top-left corner cell - sticky in both directions */
    thead tr th:first-child {
        z-index: 15 !important;
        border-right: 2px solid ${theme.colors.darkSpace80};
        border-bottom: 2px solid ${theme.colors.darkSpace80};
    }
`;


const ObjectUserRestrictionTable = ({venueCustomerId, venueUsers}) => {

    const userContext = useContext(UserContext);
    const [venueZones, setVenueZones] = useState([]);
    const [venueMusicPlayers, setVenueMusicPlayers] = useState([]);
    const [venueRestrictions, setVenueRestrictions] = useState([]);

    useEffect(() => {
        if (!venueCustomerId) return;
        fetchVenueZones();
        fetchVenueMusicPlayers();
        fetchVenueRestrictions();
    }, [userContext, venueCustomerId]);

    const fetchVenueZones = async () => {
        const zones = await getZones(venueCustomerId);
        const visibleZones = zones.filter(zone => !zone.hide);
        setVenueZones(visibleZones);
    }

    const fetchVenueMusicPlayers = async () => {
        const musicPlayers = await getMusicPlayers(venueCustomerId);
        setVenueMusicPlayers(musicPlayers);
    }

    const fetchVenueRestrictions = async () => {
        const restrictions = await getUserObjectRestrictionsByCustomer(venueCustomerId);
        const hiddenZoneIds = venueZones.filter(z => z.hide).map(z => z.zoneId);
        const visibleRestrictions = restrictions.filter(r => !(r.objectType === 'ZONE' && hiddenZoneIds.includes(Number(r.objectId))));
        setVenueRestrictions(visibleRestrictions);
    }

    // Check if a restriction exists for a user-zone combination
    const hasRestriction = (userId, zoneId) => {
        
        const exists = venueRestrictions.some(restriction => {
            const match = restriction.userId.toString() === userId.toString() && 
                         restriction.objectId.toString() === zoneId.toString();
            return match;
        });
        
        return exists;
    }

    // Handle checkbox change - inverse logic: clicking removes access (adds restriction) when currently has access
    const handleCheckboxChange = async (userId, objectId, currentlyRestricted, objectType = 'ZONE') => {
        try {
            if (currentlyRestricted) {
                // Currently restricted, clicking will grant access (remove restriction)
                const restriction = venueRestrictions.find(r => 
                    r.userId.toString() === userId.toString() && 
                    r.objectId.toString() === objectId.toString()
                );
                if (restriction?.restrictionId) {
                    await deleteUserObjectRestriction(restriction.restrictionId);
                }
            } else {
                // Currently has access, clicking will restrict access (add restriction)
                const newRestriction = {
                    customerId: venueCustomerId,
                    userId: userId,
                    objectId: objectId,
                    objectType: objectType,
                    createdByUserId: userContext.user.userId,
                    createdByUserName: userContext.user.firstName + ' ' + userContext.user.lastName
                };
                await addUserObjectRestriction(newRestriction);
            }
            // Refresh restrictions after change
            await fetchVenueRestrictions();
        } catch (error) {
            console.error('Failed to update restriction:', error);
        }
    }

    // Filter out current user from the list
    const filteredUsers = venueUsers.filter(user => user.userId !== userContext.user.userId);

    return (<>
        {!filteredUsers?.length && <Placeholder><p>No other users found for this venue</p></Placeholder>}

        {!venueZones?.length && <Placeholder><p>No zones found for this venue</p></Placeholder>}

        {filteredUsers?.length > 0 && venueZones?.length > 0 && (
            <TableContainer>
                <StickyTable>
                    <thead>
                        <HeaderRow>
                            <th style={{ width: '200px', color: theme.colors.darkSpace20 }}>
                                User Restrictions
                            </th>
                        {venueZones.map(zone => (
                            <ZoneHeaderCell key={zone.zoneId} as="th">
                                <div style={{ color: theme.colors.darkSpace20 }}>
                                    {zone.zoneName || `Zone ${zone.zoneId}`}
                                </div>
                                <div style={{ color: theme.colors.darkSpace60, fontSize: '12px', fontWeight: 'normal' }}>
                                    Zone
                                </div>
                            </ZoneHeaderCell>
                        ))}
                        {venueMusicPlayers.map(player => (
                            <ZoneHeaderCell key={player.musicPlayerId} as="th">
                                <div style={{ color: theme.colors.darkSpace20 }}>
                                    {player.name || `Player ${player.musicPlayerId}`}
                                </div>
                                <div style={{ color: theme.colors.darkSpace60, fontSize: '12px', fontWeight: 'normal' }}>
                                    Music Player
                                </div>
                            </ZoneHeaderCell>
                        ))}
                    </HeaderRow>
                </thead>
                <tbody>
                    {filteredUsers.map(user => (
                        <tr key={user.userId}>
                            <UserCell>
                                {getUserDisplayName(user)}
                            </UserCell>
                            {venueZones.map(zone => {
                                const isRestricted = hasRestriction(user.userId, zone.zoneId);
                                const hasAccess = !isRestricted; // Inverse: checkmark shows when NO restriction
                                return (
                                    <CheckboxCell key={`${user.userId}-${zone.zoneId}`}>
                                        <RestrictionCheckbox
                                            checked={hasAccess}
                                            onClick={() => handleCheckboxChange(user.userId, zone.zoneId, isRestricted, 'ZONE')}
                                        >
                                            <CheckIcon viewBox='0 0 24 24' visible={hasAccess}>
                                                <polyline points="20 4 9 15 4 10" />
                                            </CheckIcon>
                                        </RestrictionCheckbox>
                                    </CheckboxCell>
                                );
                            })}

                            {venueMusicPlayers.map(player => {
                                const isRestricted = hasRestriction(user.userId, player.musicPlayerId);
                                const hasAccess = !isRestricted; // Inverse: checkmark shows when NO restriction
                                return (
                                    <CheckboxCell key={`${user.userId}-${player.musicPlayerId}`}>
                                        <RestrictionCheckbox
                                            checked={hasAccess}
                                            onClick={() => handleCheckboxChange(user.userId, player.musicPlayerId, isRestricted, 'MUSICPLAYER')}
                                        >
                                            <CheckIcon viewBox='0 0 24 24' visible={hasAccess}>
                                                <polyline points="20 4 9 15 4 10" />
                                            </CheckIcon>
                                        </RestrictionCheckbox>
                                    </CheckboxCell>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
                </StickyTable>
            </TableContainer>
        )}
    </>)
}

export default ObjectUserRestrictionTable;